import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import Layout, { DateInput } from "./Layout";
import { formatDate, getApiUrl, getTodayIso } from "./config";

function Expenses() {
  const today = getTodayIso();
  const [expenseAccounts, setExpenseAccounts] = useState([]);
  const [expense, setExpense] = useState({
    date: today,
    account_id: "",
    account_name: "",
    purpose: "",
    amount: 0,
    payment_mode: "Cash",
  });
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [rows, setRows] = useState([]);

  const fetchExpenses = useCallback(async () => {
    try {
      const res = await axios.get(`${getApiUrl()}/expenses`, { params: { from, to } });
      setRows(res.data || []);
    } catch (err) {
      console.error(err);
    }
  }, [from, to]);

  const fetchExpenseAccounts = useCallback(async () => {
    try {
      const res = await axios.get(`${getApiUrl()}/accounts`, { params: { type: "expense" } });
      setExpenseAccounts((res.data || []).filter((a) => a.active));
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  useEffect(() => {
    fetchExpenseAccounts();
  }, [fetchExpenseAccounts]);

  const updateExpenseAccount = (value) => {
    const normalized = String(value || "").trim().toLowerCase();
    const matchedAccount = expenseAccounts.find((a) => a.name.trim().toLowerCase() === normalized);

    setExpense((current) => ({
      ...current,
      account_id: matchedAccount ? String(matchedAccount.id) : "",
      account_name: value,
    }));
  };

  const saveExpense = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${getApiUrl()}/expenses`, {
        ...expense,
        account_id: expense.account_id ? Number(expense.account_id) : undefined,
        amount: parseFloat(expense.amount) || 0,
      });
      alert("Expense saved");
      setExpense({ date: today, account_id: "", account_name: "", purpose: "", amount: 0, payment_mode: "Cash" });
      fetchExpenseAccounts();
      fetchExpenses();
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.sqlMessage ||
        err?.response?.data?.code ||
        "Failed to save expense";
      alert(message);
    }
  };

  return (
    <Layout>
      <div className="container mt-4">
        <h2>Expenses</h2>

        <form onSubmit={saveExpense} className="card p-4 mb-4">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Date</label>
              <DateInput value={expense.date} onChange={(v) => setExpense({ ...expense, date: v })} required />
            </div>
            <div className="col-md-3">
              <label className="form-label">Activity (Account Name)</label>
              <input
                type="text"
                className="form-control"
                list="expense-account-options"
                value={expense.account_name}
                onChange={(e) => updateExpenseAccount(e.target.value)}
                onFocus={(e) => e.target.select()}
                placeholder="Select or type new account"
                required
              />
              <datalist id="expense-account-options">
                {expenseAccounts.map(a => (
                  <option key={a.id} value={a.name} />
                ))}
              </datalist>
            </div>
            <div className="col-md-3">
              <label className="form-label">Payment Mode</label>
              <select
                className="form-select"
                value={expense.payment_mode}
                onChange={(e) => setExpense({ ...expense, payment_mode: e.target.value })}
              >
                <option value="Cash">Cash</option>
                <option value="UPI">UPI</option>
                <option value="Bank Transfer">Bank Transfer</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Paid Amount</label>
              <input
                type="number"
                className="form-control"
                value={expense.amount}
                onChange={(e) => setExpense({ ...expense, amount: e.target.value })}
                onFocus={(e) => e.target.select()}
                required
              />
            </div>
            <div className="col-12">
              <label className="form-label">Purpose</label>
              <input
                type="text"
                className="form-control"
                value={expense.purpose}
                onChange={(e) => setExpense({ ...expense, purpose: e.target.value })}
                onFocus={(e) => e.target.select()}
              />
            </div>
            <div className="col-12">
              <button type="submit" className="btn btn-warning btn-lg">
                Save Expense
              </button>
            </div>
          </div>
        </form>

        <div className="card p-4">
          <div className="row g-3 align-items-end mb-3">
            <div className="col-md-3">
              <label className="form-label">From</label>
              <DateInput value={from} onChange={setFrom} />
            </div>
            <div className="col-md-3">
              <label className="form-label">To</label>
              <DateInput value={to} onChange={setTo} />
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Account</th>
                  <th>Purpose</th>
                  <th>Mode</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>{formatDate(r.date)}</td>
                    <td>{r.account_name}</td>
                    <td>{r.purpose}</td>
                    <td>{r.payment_mode}</td>
                    <td className="fw-bold">₹ {(parseFloat(r.amount) || 0).toFixed(2)}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan="5" className="text-center">
                      No expenses
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <th colSpan="4" className="text-end">
                    Total
                  </th>
                  <th>
                    ₹ {rows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0).toFixed(2)}
                  </th>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default Expenses;
